import { useCallback, useEffect, useState } from "react";
import {
  CoachAthleteEdge as EdgeSchema,
  type CoachAthleteEdge,
  type CoachEdgeGroups,
  type CoachGrant,
  emptyCoachEdgeGroups,
  groupCoachEdges,
} from "@heraion/shared";

import { apiFetch, ApiError } from "./api";
import { useAuth } from "./auth/AuthContext";
import { onLateSession } from "./auth/cognito";

// Web-rnw coach-edges surface: fetch the caller's edges, bucket them with the
// SHARED groupCoachEdges (same six groups mobile's context computes), and expose
// invite / accept / revoke mutations. The web experience is coach-primary — it
// carries the invite-by-email form that mobile deliberately omits (mobile is
// athlete-side accept/decline). Grouping is shared; the fetch + mutation wiring
// is per-platform (web apiFetch vs mobile's).

export type EdgeActionResult = { ok: boolean; code?: string };
type Status = "loading" | "ready" | "error";

const extractCode = (err: unknown): string => {
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null;
    if (body && typeof body.error === "string") return body.error;
  }
  return "io_error";
};

export function useCoachEdgesWeb() {
  const { state } = useAuth();
  const callerSub = state.status === "signed_in" ? state.user.sub : null;
  const [status, setStatus] = useState<Status>("loading");
  const [groups, setGroups] = useState<CoachEdgeGroups>(emptyCoachEdgeGroups());
  // Peer sub → display name, resolved from the edge-gated coach profile
  // projection. Only athletes the caller COACHES resolve (the gate allows
  // reading their profile); a coach who coaches the caller 403s and stays on
  // the email/sub fallback. Best-effort — a missing name never blocks the row.
  const [names, setNames] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    if (!callerSub) {
      setGroups(emptyCoachEdgeGroups());
      setNames({});
      setStatus("ready");
      return;
    }
    try {
      const raw = await apiFetch<unknown>("/api/me/coach-edges");
      const list = Array.isArray(raw) ? raw : [];
      const edges: CoachAthleteEdge[] = [];
      for (const item of list) {
        const parsed = EdgeSchema.safeParse(item);
        if (parsed.success) edges.push(parsed.data);
      }
      setGroups(groupCoachEdges(edges, callerSub));
      setStatus("ready");

      // Resolve peer display names (best-effort, tolerant of 403/404).
      const peerSubs = Array.from(
        new Set(
          edges
            .filter((e) => e.status !== "revoked")
            .map((e) => (e.coachSub === callerSub ? e.athleteSub : e.coachSub)),
        ),
      );
      const entries = await Promise.all(
        peerSubs.map(async (sub) => {
          const p = await apiFetch<{ name?: string }>(
            `/api/profiles/${encodeURIComponent(sub)}`,
          ).catch(() => null);
          return [sub, p?.name] as const;
        }),
      );
      const map: Record<string, string> = {};
      for (const [sub, name] of entries) if (name) map[sub] = name;
      setNames(map);
    } catch (err) {
      // Signed-out / token-expired → empty, not an error banner.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        setGroups(emptyCoachEdgeGroups());
        setStatus("ready");
        return;
      }
      setStatus("error");
    }
  }, [callerSub]);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  // An offline boot read the edges with no session: its session reads again.
  useEffect(() => onLateSession(refresh), [refresh]);

  const invite = useCallback(
    async (email: string, note?: string): Promise<EdgeActionResult> => {
      try {
        await apiFetch("/api/me/coach-edges", {
          method: "POST",
          body: {
            peerEmail: email,
            inviteNote: note && note.length > 0 ? note : undefined,
            inviterRole: "coach",
          },
        });
        await refresh();
        return { ok: true };
      } catch (err) {
        return { ok: false, code: extractCode(err) };
      }
    },
    [refresh],
  );

  const act = useCallback(
    async (edgeId: string, path: "accept" | "revoke"): Promise<EdgeActionResult> => {
      try {
        await apiFetch(`/api/me/coach-edges/${edgeId}/${path}`, {
          method: "PATCH",
        });
        await refresh();
        return { ok: true };
      } catch (err) {
        return { ok: false, code: extractCode(err) };
      }
    },
    [refresh],
  );

  // #27: the athlete sets what one coach may do (the whole list).
  const setGrants = useCallback(
    async (edgeId: string, grants: CoachGrant[]): Promise<EdgeActionResult> => {
      try {
        await apiFetch(`/api/me/coach-edges/${edgeId}/grants`, {
          method: "PATCH",
          body: { grants },
        });
        await refresh();
        return { ok: true };
      } catch (err) {
        return { ok: false, code: extractCode(err) };
      }
    },
    [refresh],
  );

  return { status, groups, callerSub, names, refresh, invite, act, setGrants };
}
